import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface PlaceOrderItem {
    item_id?: number | null;
    item_code?: string | null;
    item_name?: string | null;
    quantity: number;
    unit_price: number;
    original_price?: number | null;
    discount?: number | null;
    discount_amount?: number | null;
    amount: number;
}

export interface PlaceOrderRequest {
    items: PlaceOrderItem[];
    payment_received?: number;
    member?: string | null;
    rep?: string | null;
    pricemode?: string | null;
    refno?: string | null;
}

export interface PlaceOrderResponse {
    order_no: string;
    item_count: number;
    amount: number;
    created_at: string;
}

export interface RecentOrderItemResponse {
    item_id?: number | null;
    item_code?: string | null;
    item_name?: string | null;
    quantity: number;
    unit_price: number;
    original_price?: number | null;
    discount?: number | null;
    discount_amount?: number | null;
    amount: number;
}

export interface RecentOrderResponse {
    order_no: string;
    item_count: number;
    amount: number;
    created_at?: string | null;
    member?: string | null;
    rep?: string | null;
    items: RecentOrderItemResponse[];
}

export const placeOrder = async (payload: PlaceOrderRequest): Promise<PlaceOrderResponse> => {
    try {
        const response: ApiResponse<PlaceOrderResponse | null> = (
            await apiClient.post("/pos_orders/place", payload, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Order request completed without a response payload.");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === "string" ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to place order");
    }
};

export const updateOrder = async (
    orderNo: string,
    payload: PlaceOrderRequest,
): Promise<PlaceOrderResponse> => {
    try {
        const response: ApiResponse<PlaceOrderResponse | null> = (
            await apiClient.put(`/pos_orders/${orderNo}`, payload, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Order request completed without a response payload.");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === "string" ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to update order");
    }
};

export const getRecentOrders = async (limit = 20): Promise<RecentOrderResponse[]> => {
    try {
        const response: ApiResponse<RecentOrderResponse[] | null> = (
            await apiClient.get("/pos_orders/recent", {
                params: { limit },
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }

        throw new Error("Failed to fetch recent orders");
    }
};

export const deleteOrder = async (order_no: string): Promise<void> => {
    try {
        await apiClient.delete(`/pos_orders/${order_no}`, {
            headers: {
                "x-skip-auth-redirect": "true",
            },
        });
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }

        throw new Error("Failed to delete order");
    }
};

